<script setup lang="ts">
import { Link } from '@inertiajs/vue3';
import { computed } from 'vue';
import { useTranslations } from '@/composables/useTranslations';
import { allImageNavItems } from '@/data/imageHeaderNav';
import type { ImageMegaMenuItem } from '@/data/imageHeaderNav';
import { localizeHref } from '@/lib/localizeHref';

const props = defineProps<{
    /** Nav item key to omit (e.g. `compress`, `bulk_resize`). */
    excludeKey: string;
}>();

const { t, locale } = useTranslations();

const tools = computed((): ImageMegaMenuItem[] =>
    allImageNavItems
        .filter((item) => item.key !== props.excludeKey)
        .map((item) => ({
            ...item,
            href: localizeHref(item.href, locale.value),
        })),
);
</script>

<template>
    <section
        class="mt-6 border-t border-border/60 pt-5 sm:mt-8 sm:pt-6"
        data-testid="image-other-tools"
        aria-labelledby="image-other-tools-heading"
    >
        <div class="mb-3.5 max-w-2xl space-y-0.5">
            <h2
                id="image-other-tools-heading"
                class="text-sm font-semibold tracking-tight text-foreground"
            >
                {{
                    t('tools.image.related.heading', 'More tools on this site')
                }}
            </h2>
            <p class="text-sm leading-snug text-pretty text-muted-foreground">
                {{
                    t(
                        'tools.image.related.description',
                        'Open another workflow on Optimize Image—same limits, previews, and browser download.',
                    )
                }}
            </p>
        </div>

        <ul
            class="grid list-none gap-2 sm:grid-cols-2 lg:grid-cols-3"
            role="list"
        >
            <li v-for="tool in tools" :key="tool.key">
                <Link
                    :href="tool.href"
                    prefetch
                    class="group flex h-full touch-manipulation items-start gap-2.5 rounded-lg border border-border/70 px-3 py-2.5 ring-offset-background transition-colors outline-none hover:border-emerald-200/90 hover:bg-accent/50 focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 dark:hover:border-emerald-800/50"
                >
                    <span
                        class="flex size-8 shrink-0 items-center justify-center rounded-md bg-muted transition-colors group-hover:brightness-95"
                    >
                        <component
                            :is="tool.icon"
                            :class="['size-4', tool.iconClass]"
                            aria-hidden="true"
                        />
                    </span>
                    <span class="min-w-0 space-y-0.5">
                        <span
                            class="block text-sm leading-snug font-medium text-foreground/90 group-hover:text-foreground"
                        >
                            {{ t(`tools.image.${tool.key}.title`, tool.title) }}
                        </span>
                        <span
                            class="line-clamp-2 block text-xs leading-relaxed text-pretty text-muted-foreground"
                        >
                            {{
                                t(
                                    `tools.image.${tool.key}.description`,
                                    tool.description,
                                )
                            }}
                        </span>
                    </span>
                </Link>
            </li>
        </ul>
    </section>
</template>
